import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listPendingApplications, decideApplication } from "@/lib/applications.functions";
import { getIsAdmin } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/admin/applications")({
  head: () => ({ meta: [{ title: "Pending Applications — Admin" }] }),
  component: AdminApplicationsPage,
});

type Application = {
  id: string;
  full_name: string | null;
  email: string | null;
  quiz_score: number | null;
  quiz_completed_at: string | null;
  cv_file_path: string | null;
  cv_signed_url: string | null;
  created_at: string;
  cv_review_reason: string | null;
  cv_flagged_at: string | null;
  cv_sanitized_text: string | null;
  ai_summary: string | null;
  total_months_experience: number | null;
  candidate_score: number | null;
};

function AdminApplicationsPage() {
  const navigate = useNavigate();
  const checkAdmin = useServerFn(getIsAdmin);
  const list = useServerFn(listPendingApplications);
  const decide = useServerFn(decideApplication);
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState<Application[]>([]);
  const [actingId, setActingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const r = await list();
    if ("error" in r) {
      toast.error(r.error);
      navigate({ to: "/dashboard", replace: true });
      return;
    }
    setApps(r.applications);
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      const a = await checkAdmin();
      if (!a.isAdmin) { navigate({ to: "/dashboard", replace: true }); return; }
      await load();
    })();
  }, []);

  const handleDecide = async (applicantId: string, decision: "accept" | "deny") => {
    setActingId(applicantId);
    try {
      const r = await decide({ data: { applicant_id: applicantId, decision } });
      if ("error" in r) throw new Error(r.error);
      toast.success(decision === "accept" ? "Application accepted." : "Application denied.");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not complete action.");
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <p className="tracking-wide-luxury text-accent">Admin</p>
        <h1 className="mt-3 font-display text-3xl">Pending Applications</h1>
        <span className="gold-divider mt-4" />
        <p className="mt-4 text-sm text-muted-foreground">
          Bartenders whose CV could not be auto-approved — failed reads, unsupported files and
          borderline experience all land here. Review each candidate, then approve or deny.
        </p>

        {loading ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : apps.length === 0 ? (
          <div className="mt-12 border border-border bg-card p-8 text-center">
            <p className="font-display text-lg">All caught up.</p>
            <p className="mt-2 text-sm text-muted-foreground">No applications are waiting for review.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-8">
            {apps.map((a) => (
              <article key={a.id} className="border border-border bg-card p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-xl">{a.full_name || "Unnamed applicant"}</p>
                    <p className="text-xs text-muted-foreground">{a.email || "—"}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-4 text-xs tracking-wide-luxury text-muted-foreground">
                      <span>
                        Flagged:{" "}
                        {a.cv_flagged_at
                          ? new Date(a.cv_flagged_at).toLocaleString()
                          : new Date(a.created_at).toLocaleString()}
                      </span>
                      {typeof a.total_months_experience === "number" && (
                        <span>Experience detected: {a.total_months_experience} months</span>
                      )}
                      {typeof a.candidate_score === "number" && (
                        <span>AI score: {a.candidate_score}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      disabled={actingId === a.id}
                      onClick={() => handleDecide(a.id, "accept")}
                      className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury"
                    >
                      Approve Manually
                    </Button>
                    <Button
                      disabled={actingId === a.id}
                      variant="outline"
                      onClick={() => handleDecide(a.id, "deny")}
                      className="rounded-none border-destructive text-destructive hover:bg-destructive/10 tracking-wide-luxury"
                    >
                      Deny Application
                    </Button>
                  </div>
                </div>

                {a.cv_review_reason && (
                  <div className="mt-6 border-l-2 border-accent bg-muted/40 p-4">
                    <p className="text-xs tracking-wide-luxury text-muted-foreground">WHY IT NEEDS REVIEW</p>
                    <p className="mt-1 text-sm">{a.cv_review_reason}</p>
                  </div>
                )}

                {a.ai_summary && (
                  <div className="mt-4">
                    <p className="text-xs tracking-wide-luxury text-muted-foreground">AI SUMMARY</p>
                    <p className="mt-1 text-sm text-muted-foreground">{a.ai_summary}</p>
                  </div>
                )}

                {a.cv_sanitized_text && (
                  <details className="mt-4 border border-border p-4">
                    <summary className="cursor-pointer text-xs tracking-wide-luxury text-muted-foreground">
                      REDACTED CV TEXT
                    </summary>
                    <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap text-xs text-muted-foreground">
                      {a.cv_sanitized_text}
                    </pre>
                  </details>
                )}


                <div className="mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs tracking-wide-luxury text-muted-foreground">CV PREVIEW</p>
                    {a.cv_signed_url && (
                      <a
                        href={a.cv_signed_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs tracking-wide-luxury text-accent hover:text-accent/80"
                      >
                        Open in new tab →
                      </a>
                    )}
                  </div>
                  {a.cv_signed_url ? (
                    <iframe
                      src={a.cv_signed_url}
                      title={`CV — ${a.full_name || a.id}`}
                      className="w-full h-[600px] border border-border bg-white"
                    />
                  ) : (
                    <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                      No CV file on record.
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
